import React, { useEffect, useState } from 'react'

import { H2, H3 } from '@piconetworks/pkg-heading'
import ToggleBar from '@piconetworks/pkg-toggle-bar'
import Boutton from '@piconetworks/pkg-boutton'
import { format } from '@piconetworks/pkg-currency'

import { useFeatureManager } from 'features/FeatureManager'
import useUserId from 'hooks/useUserId'
import usePublisherId from 'hooks/usePublisherId'

import AddonPurchaser from './IntegrationPurchaser'

import styles from './PlanDetail.module.scss'

const PlanItem = ({
    creditAmount = '',
    creditType = '',
    tierName = '',
    pricingInterval = '',
    pricingAmount = '',
}) => {
    const abbrIntervalMap = {
        'month': 'mo',
        'year': 'yr',
    }
    const intervalAbbr = abbrIntervalMap[pricingInterval]

    return (
        <div className={styles.planItem}>
            <div>
                <H3>{tierName}</H3>
                <div className={styles.credits}>{creditAmount} {creditType} credits per month</div>
            </div>
            <div className={styles.price}>${pricingAmount} <span>/{intervalAbbr}</span></div>
        </div>
    )
}

const PlanDetail = ({
    onChange = (e = null) => {},
    intervals = [],
    defaultInterval = null,
}) => {
    return (
        <ToggleBar.Group
            className={styles.interval}
            defaultValue={defaultInterval}
            name='planDetail'
            onChange={onChange}
            variant="primary"
        >
            {intervals?.reduce((acc, interval) => {
                const properIntervalMap = {
                    'month': 'Monthly',
                    'year': 'Annual',
                }

                const intervalProper = properIntervalMap[interval]

                if (!intervalProper) {
                    return acc
                }

                acc.push(
                    <ToggleBar.Item key={interval} value={interval}>{intervalProper}</ToggleBar.Item>
                )

                return acc
            }, [])}
        </ToggleBar.Group>
    )
}

const PlanPicker = ({
    onChange = (e = null) => {},
    plans = [],
    defaultPricing = null,
    subscribedToPricingId = null
}) => {
    plans.sort(({ pricingAmount: a } = {}, { pricingAmount: b } = {}) => a - b)

    return (
        <ToggleBar.Group
            defaultValue={defaultPricing}
            isVertical
            key={`_${defaultPricing}`}
            name='planPicker'
            onChange={onChange}
            variant={"primary"}
        >
            {plans?.reduce((acc, plan) => {
                const pricingId = plan.pricingId
                const formattedCreditAmount = Intl.NumberFormat('en-US').format(Number(plan.creditAmount))

                if (!pricingId) {
                    return acc
                }

                acc.push(
                    <ToggleBar.Item key={plan.pricingId} value={plan.pricingId} disabled={subscribedToPricingId === plan.pricingId}>
                        <PlanItem
                            creditAmount={formattedCreditAmount}
                            creditType={plan.creditType}
                            tierName={plan.tierName}
                            pricingInterval={plan.pricingInterval}
                            pricingAmount={plan.pricingAmount}
                        />
                    </ToggleBar.Item>
                )

                return acc
            }, [])}
        </ToggleBar.Group>
    )
}

// a Store page with a CTA that when clicked triggers the Cart
const PlanListing = ({
    appLabel = null,
    hideUpgrade = () => {},
    subscribedToPricingId = '',
}) => {
    const [currentInterval, setInterval] = useState(null)
    const [selectedPricingId, selectPricingId] = useState(null)

    const userId = useUserId()
    const publisherId = usePublisherId()
    const { getAppByLabel, getAppTierByPricingId } = useFeatureManager({
        userId,
        publisherId,
    })

    const app = getAppByLabel(appLabel)
    const tiers = app?.tiers

    const intervals = Object.keys(tiers?.reduce((acc, tier) => {
        tier?.pricings?.forEach((pricing) => {
            const interval = pricing?.interval

            if (interval) {
                acc[interval] = null
            }
        })


        return acc
    }, {})).sort()

    const defaultInterval = intervals?.[0]

    const planItems = tiers?.reduce((acc, tier) => {
        const planItem = {
            tierName: '',
            creditAmount: 0,
            creditType: '',
            hasAppTierAccess: false,
            pricingInterval: '',
            pricingAmount: 0,
            pricingId: '',
        }

        planItem.hasAppTierAccess = tier?.has_access
        planItem.tierName = tier?.name

        const credit = tier?.credits?.[0]
        const pricing = tier?.pricings?.find((pricing) => pricing?.interval === (currentInterval || defaultInterval))

        if (pricing) {
            planItem.creditAmount = credit?.amount
            planItem.creditType = credit?.type
            planItem.pricingAmount = format({ amount: pricing?.amount, currency: pricing?.pricingCurrency, symbol: false })
            planItem.pricingInterval = pricing?.interval
            planItem.pricingId = pricing?.id
            planItem.hasAppTierAccess = tier?.has_access && !pricing?.subscription?.canceled_at

            if (!(pricing?.id?.includes('free-99'))){
                acc.push(planItem)
            }
        }

        return acc
    } , [])

    const firstPricingId = planItems?.[0]?.pricingId
    const secondPricingId = planItems?.[1]?.pricingId
    const defaultPricing = subscribedToPricingId !== firstPricingId ? firstPricingId : secondPricingId

    useEffect(() => {
        selectPricingId?.(defaultPricing)
    }, [defaultPricing, selectPricingId])

    const oldAppTier = getAppTierByPricingId(subscribedToPricingId)
    const newAppTier = getAppTierByPricingId(selectedPricingId)

    const oldPricing = oldAppTier?.getPricingById(subscribedToPricingId)
    const newPricing = newAppTier?.getPricingById(selectedPricingId)

    const properIntervalMap = {
        'month': 'Monthly',
        'year': 'Annual',
    }

    const newAppTierName = (oldAppTier?.id !== newAppTier?.id) && newAppTier?.name
    const newInterval = (oldPricing?.interval !== newPricing?.interval) && properIntervalMap[newPricing?.interval]

    const switchToWording = newAppTierName || newInterval

    return (
        <section className={styles.planContainer}>
            <div className={styles.header}>
            <H2>Subscription</H2>
            <PlanDetail
                intervals={intervals}
                onChange={setInterval}
                defaultInterval={defaultInterval}
            />
            </div>
            <PlanPicker
                plans={planItems}
                onChange={selectPricingId}
                defaultPricing={selectedPricingId}
                subscribedToPricingId={subscribedToPricingId}
            />
            <div className={styles.cta}>
                <Boutton
                    className={styles.btnCancel}
                    onClick={hideUpgrade}
                >
                    Cancel
                </Boutton>
                <AddonPurchaser
                    pricingId={selectedPricingId}
                >
                    Switch to {switchToWording}
                </AddonPurchaser>
            </div>
        </section>
    )
}
export default PlanListing
